import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
    getMbDiscoveryService,
    saveMbDiscoveryService,
} from "../../../../Services/CRMAdminServices/CRMMbDiscoveryService";

/**
 * Discovery call — mortgage broking business.
 *
 * Every question on Richard's DC sheet, in the order he asks them. The screen
 * follows the conversation rather than the database: who they are, what the
 * business looks like today against twelve months out, who does the work, what
 * is in the way, and what it is costing them at home.
 *
 * Nothing is mandatory. A call where half the answers never came up still
 * saves, because a half-filled record beats one he has to re-ask.
 *
 * The three numbers at the top are worked out from the answers, not typed. They
 * are the argument the proposal gets made from, so they have to come from what
 * the broker actually said.
 */

const STATES = ["", "VIC", "NSW", "QLD", "SA", "WA", "TAS", "NT", "ACT"];
const GOALS = ["", "Reduce hours", "Build the business", "Both"];

const money = (v: any) =>
    v === null || v === undefined || v === ""
        ? "—"
        : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 })
            .format(Number(v));

const CRMMbDiscovery = () => {
    const { leadID } = useParams();
    const leadId = Number(leadID);

    const [d, setD] = useState<any>({ leadID: leadId });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        if (!leadId) return;
        setLoading(true);
        try {
            const r: any = await getMbDiscoveryService(leadId);
            const data = r?.data ?? {};
            setD({
                ...data,
                leadID: leadId,
                discoveryDate: data.discoveryDate
                    ? String(data.discoveryDate).slice(0, 10)
                    : new Date().toISOString().slice(0, 10),
            });
            setDirty(false);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not load the discovery call.");
        } finally {
            setLoading(false);
        }
    }, [leadId]);

    useEffect(() => { load(); }, [load]);

    const set = (key: string, value: any) => {
        setD((x: any) => ({ ...x, [key]: value }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            await saveMbDiscoveryService({
                ...d,
                leadID: leadId,
                discoveryDate: d.discoveryDate || null,
                // Empty boxes are nothing, not zero. A broker who did not answer
                // "how many staff" has not told us they have none.
                yearsBroking: num(d.yearsBroking),
                averageLoanSize: num(d.averageLoanSize),
                settlementsUnitsToday: num(d.settlementsUnitsToday),
                settlementsUnitsNext: num(d.settlementsUnitsNext),
                settlementsValueToday: num(d.settlementsValueToday),
                settlementsValueNext: num(d.settlementsValueNext),
                loanBookToday: num(d.loanBookToday),
                loanBookNext: num(d.loanBookNext),
                hoursPerWeekToday: num(d.hoursPerWeekToday),
                hoursPerWeekNext: num(d.hoursPerWeekNext),
                staffCount: num(d.staffCount),
                clientCount: num(d.clientCount),
            });
            toast.success("Saved.");
            setDirty(false);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not save.");
        } finally {
            setSaving(false);
        }
    };

    // The three numbers the proposal is argued from.
    const worked = useMemo(() => {
        const unitsGap = gap(d.settlementsUnitsNext, d.settlementsUnitsToday);
        const hoursBack = gap(d.hoursPerWeekToday, d.hoursPerWeekNext);
        const avg = num(d.averageLoanSize);
        const extra = unitsGap !== null && unitsGap > 0 && avg ? unitsGap * avg * 12 : null;
        return { unitsGap, hoursBack, extra };
    }, [d]);

    if (loading) return <div className="text-center py-5"><Spinner size="sm" /></div>;

    return (
        <div className="p-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="f-w-900 mb-0" style={{ color: "blue" }}>
                    Discovery call — broking business
                </h3>
                <div className="d-flex align-items-center gap-2">
                    {dirty && <small className="text-muted">Unsaved</small>}
                    <Button color="primary" size="sm" disabled={saving} onClick={save}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>

            {/* What the answers add up to */}
            <div className="d-flex mb-3" style={{ gap: 12, flexWrap: "wrap" }}>
                <Tile label="More settlements a month"
                    value={worked.unitsGap === null ? "—" : worked.unitsGap} strong={!!worked.unitsGap} />
                <Tile label="Hours a week they want back"
                    value={worked.hoursBack === null ? "—" : worked.hoursBack} />
                <Tile label="Extra settlement value a year"
                    value={money(worked.extra)} strong={!!worked.extra} />
            </div>

            <Panel title="Who are they">
                <Row>
                    <Field label="Date" w={2}>
                        <input type="date" className="form-control form-control-sm"
                            value={d.discoveryDate ?? ""} onChange={(e) => set("discoveryDate", e.target.value)} />
                    </Field>
                    <Field label="State" w={2}>
                        <select className="form-select form-select-sm"
                            value={d.stateCode ?? ""} onChange={(e) => set("stateCode", e.target.value)}>
                            {STATES.map((s) => <option key={s || "none"} value={s}>{s || "—"}</option>)}
                        </select>
                    </Field>
                    <Field label="Mobile" w={3}>
                        <input className="form-control form-control-sm"
                            value={d.mobile ?? ""} onChange={(e) => set("mobile", e.target.value)} />
                    </Field>
                    <Field label="Email" w={5}>
                        <input className="form-control form-control-sm"
                            value={d.email ?? ""} onChange={(e) => set("email", e.target.value)} />
                    </Field>
                </Row>
                <Row>
                    <Field label="Years in broking" w={2}>
                        <input type="number" className="form-control form-control-sm"
                            value={d.yearsBroking ?? ""} onChange={(e) => set("yearsBroking", e.target.value)} />
                    </Field>
                    <Field label="Aggregator" w={4}>
                        <input className="form-control form-control-sm"
                            value={d.aggregator ?? ""} onChange={(e) => set("aggregator", e.target.value)} />
                    </Field>
                    <Field label="Average loan size" w={3}>
                        <input type="number" className="form-control form-control-sm"
                            value={d.averageLoanSize ?? ""} onChange={(e) => set("averageLoanSize", e.target.value)} />
                    </Field>
                    <Field label="What they want" w={3}>
                        <select className="form-select form-select-sm"
                            value={d.goal ?? ""} onChange={(e) => set("goal", e.target.value)}>
                            {GOALS.map((g) => <option key={g || "none"} value={g}>{g || "—"}</option>)}
                        </select>
                    </Field>
                </Row>
            </Panel>

            <Panel title="What the business looks like">
                <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: "40%" }} />
                                <th style={{ width: "30%" }}>Today</th>
                                <th style={{ width: "30%" }}>Next 12 months</th>
                            </tr>
                        </thead>
                        <tbody>
                            <Pair label="Settlements a month — number" unit
                                a={d.settlementsUnitsToday} b={d.settlementsUnitsNext}
                                onA={(v: any) => set("settlementsUnitsToday", v)}
                                onB={(v: any) => set("settlementsUnitsNext", v)} />
                            <Pair label="Settlements a month — value"
                                a={d.settlementsValueToday} b={d.settlementsValueNext}
                                onA={(v: any) => set("settlementsValueToday", v)}
                                onB={(v: any) => set("settlementsValueNext", v)} />
                            <Pair label="Loan book"
                                a={d.loanBookToday} b={d.loanBookNext}
                                onA={(v: any) => set("loanBookToday", v)}
                                onB={(v: any) => set("loanBookNext", v)} />
                            <Pair label="Hours worked a week" unit
                                a={d.hoursPerWeekToday} b={d.hoursPerWeekNext}
                                onA={(v: any) => set("hoursPerWeekToday", v)}
                                onB={(v: any) => set("hoursPerWeekNext", v)} />
                        </tbody>
                    </table>
                </div>
            </Panel>

            <Panel title="Support staff">
                <Row>
                    <Field label="How many staff" w={3}>
                        <input type="number" className="form-control form-control-sm"
                            value={d.staffCount ?? ""} onChange={(e) => set("staffCount", e.target.value)} />
                    </Field>
                    <Field label="Who does the data entry and loan processing" w={4}>
                        <input className="form-control form-control-sm"
                            value={d.dataEntryBy ?? ""} onChange={(e) => set("dataEntryBy", e.target.value)} />
                    </Field>
                    <Field label="Who does the credit assessment / Quickli" w={5}>
                        <input className="form-control form-control-sm"
                            value={d.creditAssessmentBy ?? ""} onChange={(e) => set("creditAssessmentBy", e.target.value)} />
                    </Field>
                </Row>
                <Row>
                    <Field label="How many clients and leads" w={3}>
                        <input type="number" className="form-control form-control-sm"
                            value={d.clientCount ?? ""} onChange={(e) => set("clientCount", e.target.value)} />
                    </Field>
                    <Field label="Who looks after the clients" w={4}>
                        <input className="form-control form-control-sm"
                            value={d.clientsLookedAfterBy ?? ""} onChange={(e) => set("clientsLookedAfterBy", e.target.value)} />
                    </Field>
                    <Field label="Which task takes the most time" w={5}>
                        <input className="form-control form-control-sm"
                            value={d.biggestTimeTask ?? ""} onChange={(e) => set("biggestTimeTask", e.target.value)} />
                    </Field>
                </Row>
            </Panel>

            <Panel title="Roadblocks">
                <Row>
                    <Field label="Main roadblock to reaching those numbers" w={8}>
                        <input className="form-control form-control-sm"
                            value={d.mainRoadblock ?? ""} onChange={(e) => set("mainRoadblock", e.target.value)} />
                    </Field>
                    <Field label="How long has it been an issue" w={4}>
                        <input className="form-control form-control-sm"
                            value={d.roadblockHowLong ?? ""} onChange={(e) => set("roadblockHowLong", e.target.value)} />
                    </Field>
                </Row>
                <Row>
                    <Field label="What have they tried, and what happened" w={12}>
                        <textarea className="form-control form-control-sm" rows={2}
                            value={d.triedAndResult ?? ""} onChange={(e) => set("triedAndResult", e.target.value)} />
                    </Field>
                </Row>
            </Panel>

            <Panel title="Pain and consequences">
                <Row>
                    <Field label="What happens to files and client follow-up when they get busy" w={6}>
                        <textarea className="form-control form-control-sm" rows={2}
                            value={d.whenBusyWhatHappens ?? ""} onChange={(e) => set("whenBusyWhatHappens", e.target.value)} />
                    </Field>
                    <Field label="What it costs them — evenings, weekends, family" w={6}>
                        <textarea className="form-control form-control-sm" rows={2}
                            value={d.painToFamily ?? ""} onChange={(e) => set("painToFamily", e.target.value)} />
                    </Field>
                </Row>
            </Panel>

            <Panel title="Comments">
                <textarea className="form-control form-control-sm" rows={3}
                    value={d.comments ?? ""} onChange={(e) => set("comments", e.target.value)} />
            </Panel>

            <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                    Nothing here is required. Save whatever came up and add the rest later.
                </small>
                <Button color="primary" size="sm" disabled={saving} onClick={save}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </div>
        </div>
    );
};

// ---------- helpers ----------

const num = (v: any) => (v === "" || v === null || v === undefined ? null : Number(v));

const gap = (a: any, b: any) => {
    const x = num(a); const y = num(b);
    return x === null || y === null ? null : x - y;
};

const Panel = ({ title, children }: { title: string; children: any }) => (
    <div className="p-3 mb-3" style={{
        borderRadius: 8, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", backgroundColor: "#fff",
    }}>
        <h6 className="f-w-700 mb-3">{title}</h6>
        {children}
    </div>
);

const Row = ({ children }: { children: any }) => <div className="row g-2 mb-2">{children}</div>;

const Field = ({ label, w, children }: { label: string; w: number; children: any }) => (
    <div className={`col-12 col-md-${w}`}>
        <label className="form-label mb-1" style={{ fontSize: 12, color: "#767d95" }}>{label}</label>
        {children}
    </div>
);

const Pair = ({ label, a, b, onA, onB, unit }: any) => (
    <tr>
        <td className="f-w-600" style={{ fontSize: 13 }}>{label}</td>
        <td>
            <input type="number" className="form-control form-control-sm"
                value={a ?? ""} onChange={(e) => onA(e.target.value)} />
        </td>
        <td>
            <input type="number" className="form-control form-control-sm"
                value={b ?? ""} onChange={(e) => onB(e.target.value)} />
        </td>
    </tr>
);

const Tile = ({ label, value, strong }: { label: string; value: any; strong?: boolean }) => (
    <div style={{
        border: "1px solid #e6e8f0", borderRadius: 8, padding: "10px 16px",
        background: strong ? "#f0f9f2" : "#fff", minWidth: 170,
    }}>
        <div style={{ fontSize: 11.5, color: "#767d95", fontWeight: 600, letterSpacing: 0.3 }}>{label}</div>
        <div style={{ fontSize: 19, fontWeight: 800, color: strong ? "#1a7f37" : "#111528" }}>{value}</div>
    </div>
);

export default CRMMbDiscovery;
